import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Field, Input, Checkbox } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { toast } from '../../ui/feedback';
import { usePermissions } from '../../hooks/usePermissions';
import { finesService, errorMessage } from '../../services/finesService';

/**
 * The automatic registration's Ejar logins (owner, 3 Oct 2026: "خلى فى اعدادات التسجيل الاتوماتيك انى اقدر احط يوزر
 * وباسورد اى شخص نسجل من خلاله اتوماتيك" - several saved, "أختار واحد شغال"). The admin types any Ejar username +
 * password: the engine signs in with it first, shows the name Ejar knows it by, checks it may write fines and other
 * charges, then keeps it encrypted on the server - the password never comes back to the page. One of them (or the
 * server's own account) is in use; a login Ejar refused is never tried again until it is entered anew, so a changed
 * password cannot lock the person out of Ejar. The admin's only.
 */
const when = (t) => String(t || '').replace('T', ' ').slice(0, 16);

export default function AutoEjarAccountCard() {
    const { isAdmin } = usePermissions();          // the Admin role as the checks see it ("show the app as a role")
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');
    const [useNow, setUseNow] = useState(true);
    const [busy, setBusy] = useState(false);
    const [removing, setRemoving] = useState(null);
    const load = useCallback(async () => {
        try { setData(await finesService.ejarAutoAccounts()); } catch (e) { setData({ error: errorMessage(e) }); }
    }, []);
    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
    useEffect(() => () => setPassword(''), []);
    if (!isAdmin) return null;

    const act = async (fn, done) => {
        setBusy(true);
        try { setData(await fn()); if (done) toast.success(done); } catch (e) { toast.error(errorMessage(e)); }
        setBusy(false);
    };
    const save = async () => {
        if (!user.trim() || !password) return;
        setBusy(true);
        try {
            const r = await finesService.ejarAutoAccountSave(user.trim(), password, useNow);
            setData(r); setPassword(''); setUser(''); setOpen(false);
            toast.success(`Saved ${r.saved?.user}${r.saved?.name ? ` (${r.saved.name})` : ''}${useNow ? ' - the automatic registration writes with it now' : ''}`);
        } catch (e) { toast.error(errorMessage(e)); }
        setBusy(false);
    };
    const toggleForm = () => { setOpen((o) => !o); setPassword(''); };
    const accounts = data?.accounts || [];
    const active = data?.active || '';
    const inUse = accounts.find((a) => a.active);

    return (
        <Card className="mb-6">
            <div className="flex flex-col gap-3 text-sm text-gray-900">
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-sm font-semibold text-gray-900">Automatic registration&apos;s Ejar account</h2>
                    {data && !data.error && (inUse && !inUse.refusedAt ? (
                        <span>writes on Ejar as <b className="font-mono">{inUse.user}</b>{inUse.name ? ` (${inUse.name})` : ''}</span>
                    ) : (
                        <span>writes on Ejar as <b className="font-mono">{data.serverUser || 'the server account'}</b> (the server&apos;s own account)</span>
                    ))}
                    <Button size="sm" className="ml-auto" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />} aria-expanded={open} onClick={toggleForm}>
                        Add an Ejar login
                    </Button>
                </div>
                {data?.error && <div role="alert" className="text-sm font-medium text-red-700">{data.error}</div>}
                {inUse?.refusedAt && (
                    <div role="alert" className="text-sm font-medium text-red-700">
                        Ejar refused {inUse.user} on {when(inUse.refusedAt)} (password changed or account locked) - the server&apos;s own
                        account writes meanwhile. Add {inUse.user} again with its new password to use it.
                    </div>
                )}

                {open && (
                    <form name="auto-ejar-login" className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3"
                        onSubmit={(e) => { e.preventDefault(); save(); }}>
                        <Field label="Ejar username">
                            <Input name="username" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" className="w-full sm:w-44" />
                        </Field>
                        <Field label="Ejar password">
                            <Input name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="w-full sm:w-44" />
                        </Field>
                        <span className="pb-2"><Checkbox label="use it now" checked={useNow} onChange={(e) => setUseNow(e.target.checked)} /></span>
                        <Button type="submit" variant="primary" disabled={busy || !user.trim() || !password} isLoading={busy}>{busy ? 'Signing in…' : 'Verify & save'}</Button>
                        <span className="text-sm text-gray-600 basis-full">
                            The engine signs in to Ejar with it first, shows the name Ejar knows it by and checks it may write fines and other
                            charges; then it keeps it encrypted on the server. Every automatic registration carries the name of the login in use.
                        </span>
                    </form>
                )}

                {data && !data.error && (
                    <TableContainer>
                        <Table>
                            <TableHead><TableRow>
                                <TableHeader>Ejar login</TableHeader><TableHeader>Name on Ejar</TableHeader><TableHeader>Saved</TableHeader>
                                <TableHeader>State</TableHeader><TableHeader>Actions</TableHeader>
                            </TableRow></TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell className="font-mono">{data.serverUser || '—'}</TableCell>
                                    <TableCell>the server&apos;s own account</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>{!active || inUse?.refusedAt ? <Badge variant="success">In use</Badge> : '—'}</TableCell>
                                    <TableCell>{active && <Button size="xs" disabled={busy} onClick={() => act(() => finesService.ejarAutoAccountUse(''), "The automatic registration writes with the server's own account now")}>Use</Button>}</TableCell>
                                </TableRow>
                                {accounts.map((a) => (
                                    <TableRow key={a.user}>
                                        <TableCell className="font-mono">{a.user}</TableCell>
                                        <TableCell>{a.name || '—'}</TableCell>
                                        <TableCell className="text-xs">{when(a.savedAt)}{a.savedBy ? ` · ${a.savedBy}` : ''}</TableCell>
                                        <TableCell>
                                            {a.refusedAt
                                                ? <span className="text-xs font-semibold text-red-700" title={a.refusal}>refused by Ejar {when(a.refusedAt)} - add it again</span>
                                                : a.active ? <Badge variant="success">In use</Badge> : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <span className="inline-flex flex-wrap items-center justify-center gap-2">
                                                {!a.active && !a.refusedAt && <Button size="xs" disabled={busy} onClick={() => act(() => finesService.ejarAutoAccountUse(a.user), `The automatic registration writes as ${a.user} now`)}>Use</Button>}
                                                {removing === a.user ? (
                                                    <span role="group" aria-label={`Confirm removing ${a.user}`} className="inline-flex items-center gap-2">
                                                        <span className="font-semibold text-red-700">Remove?</span>
                                                        <Button size="xs" onClick={() => setRemoving(null)} disabled={busy}>No</Button>
                                                        <Button size="xs" variant="danger" disabled={busy}
                                                            onClick={() => { setRemoving(null); act(() => finesService.ejarAutoAccountRemove(a.user), `${a.user} removed`); }}>Yes</Button>
                                                    </span>
                                                ) : (
                                                    <Button size="xs" variant="ghost" className="text-red-700" disabled={busy} onClick={() => setRemoving(a.user)}>Remove</Button>
                                                )}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </Card>
    );
}
